import { createTheme, type MantineColorsTuple, type MantineThemeOverride } from "@mantine/core";
import {
  ACCENTS,
  DENSITIES,
  FONTS,
  NEUTRALS,
  SHADOWS,
  STATUS,
  type Accent,
  type Density,
  type DensityPreset,
  type Neutrals,
  type Scheme,
  type SlotColors,
  type StatusSlot,
  type StatusTokens,
} from "./tokens";

export type { Accent, Density, DensityPreset, Scheme, SlotColors, StatusSlot, StatusTokens };

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

// Build a 10-shade Mantine tuple from designer's 50/100/500/600 anchors.
// Indices follow Mantine convention: 0 = lightest, 5 = primary, 9 = darkest.
function buildTuple(accent: Accent): MantineColorsTuple {
  const a = ACCENTS[accent];
  return [
    a[50], //   0
    a[100], //  1
    mix(a[100], a[500], 0.25), // 2
    mix(a[100], a[500], 0.55), // 3
    mix(a[100], a[500], 0.85), // 4
    a[500], //  5  primary
    a[600], //  6
    mix(a[600], "#000000", 0.15), // 7
    mix(a[600], "#000000", 0.32), // 8
    mix(a[600], "#000000", 0.5), //  9
  ];
}

export interface SkeddTheme {
  statuses: StatusTokens;
  densities: Record<Density, DensityPreset>;
  neutrals: Record<Scheme, Neutrals>;
  brandFonts: typeof FONTS;
  defaultDensity: Density;
  defaultAccent: Accent;
}

export const SKEDD_THEME_OTHER: SkeddTheme = {
  statuses: STATUS,
  densities: DENSITIES,
  neutrals: NEUTRALS,
  brandFonts: FONTS,
  defaultDensity: "regular",
  defaultAccent: "indigo",
};

export interface CreateSkeddThemeOptions {
  primary?: Accent;
}

export function createSkeddTheme({ primary = "indigo" }: CreateSkeddThemeOptions = {}): MantineThemeOverride {
  return createTheme({
    primaryColor: "brand",
    primaryShade: { light: 5, dark: 5 },
    fontFamily: FONTS.body,
    fontFamilyMonospace: FONTS.mono,
    headings: {
      fontFamily: FONTS.heading,
      sizes: {
        h1: { fontSize: `${DENSITIES.regular.h1Size}px`, fontWeight: "600", lineHeight: "1.25" },
        h2: { fontSize: `${DENSITIES.regular.h2Size}px`, fontWeight: "600", lineHeight: "1.3" },
      },
    },
    fontSizes: {
      xs: "11px",
      sm: "12px",
      md: `${DENSITIES.regular.fontSize}px`,
      lg: "15px",
      xl: "17px",
    },
    radius: {
      xs: "3px",
      sm: "5px",
      md: "8px",
      lg: "12px",
      xl: "16px",
    },
    defaultRadius: "md",
    shadows: {
      xs: SHADOWS.light.sm,
      sm: SHADOWS.light.sm,
      md: SHADOWS.light.md,
      lg: SHADOWS.light.lg,
      xl: SHADOWS.light.lg,
    },
    colors: {
      brand: buildTuple(primary),
      indigo: buildTuple("indigo"),
      amber: buildTuple("amber"),
      green: buildTuple("green"),
      rose: buildTuple("rose"),
    },
    other: SKEDD_THEME_OTHER,
  });
}

export const theme = createSkeddTheme();

declare module "@mantine/core" {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface MantineThemeOther extends SkeddTheme {}
}
